<script setup lang="ts">
import {
  SatSetup,
  AtomCard,
  AtomText,
  AtomButton,
} from "@chatfood/design-system";
import { bemBuilder } from "@chatfood/core-utils";
import { clearError } from "nuxt/app";
import { useLocale } from "./composables/use-locale";
import { useNavigation } from "./composables/use-navigation";
import { WebFooter } from "./components/web-footer";
import { useBranding } from "./composables/use-branding";
import { useBusinessStore } from "~/stores/business";
import { useHead } from "#imports";

const css = bemBuilder("error");

const business = useBusinessStore();
const { t, setDefaultLanguage } = useLocale();
const { push } = useNavigation();
const { getHeadTags } = useBranding();

const props = defineProps<{
  error: {
    url: string;
    statusCode: number;
    statusMessage: string;
    message: string;
    data: unknown;
  };
}>();

const handleError = async () => {
  try {
    await push("order", {});
  } finally {
    clearError();
  }
};

if (business.isLoaded) {
  setDefaultLanguage(business.locale, business.languages);
  useHead(
    getHeadTags(
      business.designTokens,
      business.logo,
      business.styles?.customCssSheet,
    ),
  );
}
</script>

<template>
  <SatSetup>
    <div :class="css()">
      <div :class="css('content')">
        <AtomCard padding="large" :class="css('card')">
          <AtomText type="h1">{{ props.error.statusCode }}</AtomText>
          <AtomText tag="div" color="color-text-secondary">
            {{ props.error.message }}
          </AtomText>
          <AtomButton
            v-if="business.isLoaded"
            size="large"
            @click="handleError"
          >
            {{ t("pages.error.back_button_cta") }}
          </AtomButton>
        </AtomCard>
      </div>
      <WebFooter :class="css('footer')" />
    </div>
  </SatSetup>
</template>

<style lang="scss" scoped>
.error {
  min-width: 375px;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--color-base-100);

  @media (width > $breakpoint-sm) {
    background-color: transparent;
    flex-direction: column;
    justify-content: space-between;
    padding-block: 98px 30px;
  }

  &__content {
    max-width: 692px;
    width: 100%;
  }

  &__card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-lg);
  }

  &__footer {
    display: none;

    @media (width > $breakpoint-sm) {
      display: inherit;
    }
  }
}
</style>
